import React, { useContext } from 'react';
import { Box, Image, Media, Optional, Link } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import HeaderContext from './service/HeaderContext';
import HeaderMobileButton from './shared/HeaderMobileButton';
import RoutableSitecoreLink from '../RoutableSitecoreLink';
import SecondaryNav from './shared/SecondaryNav';
import themeStyles from './styles/Header.styles';
import { Main } from './Menu';
import useDealerStatusAndHours from './service/useDealerStatusAndHours';
import { FIND_DEALER_NAV_ITEM_MOBILE_ID } from './service/constants';
import { primaryNavItemDisplayTarget } from './service/utils';
import useHideDealerByProvinces from './service/useHideDealerByProvinces';

const WideRow = themeStyles.apply(Box, 'WideRow');
const Column = themeStyles.apply(Box, 'Column');
const MainColumn = themeStyles.apply(Box, 'WideColumn');
const MobileRightNav = themeStyles.apply(Box, 'Column');
const Logo = themeStyles.apply(Box, 'Logo');
const MinimizedLogo = themeStyles.apply(Box, 'MinimizedLogo');
const MinimizedLogoContainer = themeStyles.apply('Box', 'MinimizedLogoContainer');

const AutosHeaderRow = () => {
  const { t } = useTranslation();
  const {
    isOpenMenu,
    showPlaceIcon,
    showSecondaryMenuOnTop,
    styles,
    siteLogo,
    rightPrimaryItems,
    getPrimaryNavItemsToRender,
    minimizedLogoAlignment,
    minimizedLogoCenterAligned,
    isPrimaryLogo,
  } = useContext(HeaderContext);

  const { isDealerDataAvailable } = useDealerStatusAndHours();
  const { hideDealer } = useHideDealerByProvinces();

  const mobileRightPrimaryItems = getPrimaryNavItemsToRender(
    rightPrimaryItems,
    primaryNavItemDisplayTarget.mobile,
    true,
  );
  const notExpandedLogo = isPrimaryLogo ? siteLogo?.siteLogo : siteLogo?.secondaryLogo;

  return (
    <>
      {/* Desktop Header */}
      <Media greaterThanOrEqual="desktop">
        <WideRow>
          <Logo
            as={RoutableSitecoreLink}
            field={siteLogo?.headerUrl}
            data-testid="header-logo"
            aria-label={t('Shared.Header.siteLogoAria')}
            {...styles.logo}
          >
            <Image {...siteLogo?.siteLogo} />
          </Logo>
          <MainColumn {...styles.mainColumn} data-testid="header-top-navigation-menu">
            <Optional when={showSecondaryMenuOnTop}>
              <Box textAlign="right" mb="m" display="flex" justifyContent="end">
                <SecondaryNav />
              </Box>
            </Optional>
            <Main />
          </MainColumn>
        </WideRow>
      </Media>

      {/* Mobile Header */}
      <Media lessThan="desktop">
        <WideRow>
          <Column {...styles.logoColumn} isMenuOpen={isOpenMenu}>
            <Optional when={isOpenMenu}>
              {/* Logo container's width and height should be passed as props to MinimizedLogo styled component */}
              <MinimizedLogoContainer centerAligned={minimizedLogoCenterAligned}>
                <MinimizedLogo
                  as={RoutableSitecoreLink}
                  field={siteLogo?.headerUrl}
                  data-testid="minimized-header-logo"
                  aria-label={t('Shared.Header.siteLogoAria')}
                  width={`${siteLogo?.secondaryLogo?.width}px`}
                  height={`${siteLogo?.secondaryLogo?.height}px`}
                  minimizedLogoAlignment={minimizedLogoAlignment}
                >
                  <Image src={siteLogo?.secondaryLogo?.src} alt={siteLogo?.secondaryLogo?.alt} />
                </MinimizedLogo>
              </MinimizedLogoContainer>
            </Optional>
            <HeaderMobileButton />
          </Column>

          <Optional when={!isOpenMenu}>
            {/* Logo container's width and height should be passed as props to Logo styled component */}
            <Logo
              as={RoutableSitecoreLink}
              field={siteLogo?.headerUrl}
              data-testid="header-logo"
              aria-label={t('Shared.Header.siteLogoAria')}
              width={`${notExpandedLogo?.width}px`}
              height={`${notExpandedLogo?.height}px`}
              {...styles.logo}
            >
              <Image src={notExpandedLogo?.src} alt={notExpandedLogo?.alt} />
            </Logo>
            <Media lessThan="desktop">
              {className => (
                <MobileRightNav className={className}>
                  <SecondaryNav />
                  <Optional when={showPlaceIcon}>
                    {mobileRightPrimaryItems.map(item => {
                      // when dealer status and hours is displayed, hide Find Dealer Mobile Icon
                      if (
                        item?.itemName?.toLowerCase?.() === FIND_DEALER_NAV_ITEM_MOBILE_ID.toLowerCase() &&
                        isDealerDataAvailable &&
                        !hideDealer
                      )
                        return null;

                      return (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <Link
                          key={item?.itemId}
                          as={RoutableSitecoreLink}
                          field={item?.ctaLink}
                          data-testid={item?.itemName}
                          pr="xxs"
                        >
                          <Image
                            src={item?.icon?.src}
                            alt={item?.icon?.alt}
                            width={`${item?.icon?.width}px`}
                            height={`${item?.icon?.height}px`}
                          />
                        </Link>
                      );
                    })}
                  </Optional>
                </MobileRightNav>
              )}
            </Media>
          </Optional>
        </WideRow>
      </Media>
    </>
  );
};

export default AutosHeaderRow;
